import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const WORKSPACE_BASE = '/workspace';

type ArchiveRow = {
  id: string;
  emailNormalized: string;
  formerUserId: string;
  trigger: string;
  deletedAt: string;
  profileName: string | null;
  orderCountSnapshot: number;
};

type ListResponse = {
  items: ArchiveRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

const inputClassName =
  'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2';

export default function CustomerArchivesPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Math.min(
    100,
    Math.max(5, Number(searchParams.get('pageSize')) || 20),
  );
  const q = searchParams.get('q') ?? '';

  const [draftQ, setDraftQ] = useState(q);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const committedKey = useMemo(() => searchParams.toString(), [searchParams]);
  useEffect(() => {
    setDraftQ(searchParams.get('q') ?? '');
  }, [committedKey, searchParams]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = new URL(
          `${API_BASE}/auth/workspace/customers/deletion-archives`,
        );
        if (q.trim()) url.searchParams.set('q', q.trim());
        url.searchParams.set('page', String(page));
        url.searchParams.set('pageSize', String(pageSize));
        const res = await authorizedFetch(url.toString());
        if (!res.ok) {
          throw new Error(t('backoffice.customer_archives_error'));
        }
        const json = (await res.json()) as ListResponse;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : t('backoffice.customer_archives_error'),
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, page, pageSize, q, t]);

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      if (draftQ.trim()) next.set('q', draftQ.trim());
      else next.delete('q');
      return next;
    });
  };

  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {t('backoffice.customer_archives_title')}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            {t('backoffice.customer_archives_subtitle')}
          </p>
        </div>

        <form
          onSubmit={applySearch}
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end"
        >
          <div className="min-w-0 flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              {t('backoffice.customer_archives_search_label')}
            </label>
            <input
              type="search"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder={t('backoffice.customer_archives_search_ph')}
              className={inputClassName}
            />
          </div>
          <button
            type="submit"
            className="h-10 shrink-0 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800"
          >
            {t('backoffice.customer_archives_search_submit')}
          </button>
        </form>

        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : loading ? (
          <PaginatedListLoading skeletonRows={8} />
        ) : !data?.items.length ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
            {t('backoffice.customer_archives_empty')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      {t('backoffice.customer_archives_col_deleted')}
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      {t('backoffice.customer_archives_col_name')}
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      {t('backoffice.customer_archives_col_email')}
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      {t('backoffice.customer_archives_col_former_id')}
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      {t('backoffice.customer_archives_col_orders')}
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">
                      {t('backoffice.customer_archives_col_actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-800">
                        {new Date(row.deletedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-slate-800">
                        {row.profileName ?? '—'}
                      </td>
                      <td className="max-w-[220px] truncate px-4 py-3 font-mono text-xs text-slate-700">
                        {row.emailNormalized}
                      </td>
                      <td className="max-w-[140px] truncate px-4 py-3 font-mono text-xs text-slate-500">
                        {row.formerUserId}
                      </td>
                      <td className="px-4 py-3 text-slate-800">
                        {row.orderCountSnapshot}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <Link
                          to={`${WORKSPACE_BASE}/customers/deletion-archives/${row.id}`}
                          className="font-medium text-brand-600 hover:text-brand-700"
                        >
                          {t('backoffice.customer_archives_open')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-600">
                {t('backoffice.customer_archives_page_range', {
                  from,
                  to,
                  total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() =>
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('page', String(page - 1));
                      return next;
                    })
                  }
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  {t('backoffice.customers_page_prev')}
                </button>
                <span className="text-sm text-slate-600">
                  {t('backoffice.customers_page_of', {
                    page: data.page,
                    pages: data.totalPages,
                  })}
                </span>
                <button
                  type="button"
                  disabled={page >= data.totalPages}
                  onClick={() =>
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('page', String(page + 1));
                      return next;
                    })
                  }
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  {t('backoffice.customers_page_next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
